/**
 * Paragon Product Action Bar
 *
 * Handles the sticky PDP action bar:
 *   - Opens native <dialog> modals (enquire, appointment)
 *   - Bridges the bar's primary ADD TO BAG button to the inline product form
 *   - Auto-scrolls + flashes the variant picker when a variant is not chosen
 *   - Tracks variant-change dirty state
 *
 * Vanilla JS. No framework. Wrapped in IIFE. Loaded with `defer`.
 */
(function () {
  'use strict';

  var FLASH_DURATION_MS = 1500;

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') {
    init();
  }

  var initialized = false;
  function init() {
    if (initialized) return;
    initialized = true;

    wireModalOpeners();
    wireModalClosers();
    wirePrimaryAction();
    wireVariantDirtyTracking();
    wireVariantUpdate();
  }

  /* ───────────── Variant selection → hidden id + price sync ─────────────
     Minimal replacement for Dawn's <product-info> machinery. On every
     variant-picker change, resolves the matching variant from the product JSON
     embedded in the page and updates (a) the hidden [name=id] input and
     (b) the displayed price. No network round-trip, no URL swap.
  */
  function wireVariantUpdate() {
    var picker = document.querySelector('[data-paragon-variant-picker]');
    if (!picker) return;

    var productJsonScript = document.getElementById('paragon-product-json');
    if (!productJsonScript) return;

    var product;
    try {
      product = JSON.parse(productJsonScript.textContent);
    } catch (err) {
      return;
    }

    picker.addEventListener('change', function () {
      var selected = readSelectedOptions(picker);
      var match = product.variants.find(function (variant) {
        return variant.options.every(function (opt, idx) {
          return opt === selected[idx];
        });
      });
      if (!match) return;

      // Hidden input
      var idInput = document.querySelector('form[id^="paragon-product-form-"] [name="id"]');
      if (idInput) idInput.value = match.id;

      // Price display
      var priceEl = document.querySelector('[data-paragon-price]');
      if (priceEl && typeof match.price === 'number') {
        priceEl.textContent = formatMoney(match.price);
      }

      // Submit button availability
      var submitBtn = document.querySelector('[data-paragon-add-to-bag]');
      if (submitBtn) {
        var textEl = submitBtn.querySelector('.paragon-product-form__submit-text');
        var defaultText = submitBtn.getAttribute('data-default-text') || 'Enquire';
        if (match.available) {
          submitBtn.removeAttribute('disabled');
          if (textEl) textEl.textContent = defaultText;
        } else {
          submitBtn.setAttribute('disabled', 'disabled');
          if (textEl) textEl.textContent = 'Sold Out';
        }
      }
    });
  }

  function readSelectedOptions(picker) {
    var fieldsets = picker.querySelectorAll('fieldset[data-paragon-option-index]');
    // Sort by data-paragon-option-index so the order matches product.variants[i].options
    var sorted = Array.prototype.slice.call(fieldsets).sort(function (a, b) {
      return (
        parseInt(a.getAttribute('data-paragon-option-index'), 10) -
        parseInt(b.getAttribute('data-paragon-option-index'), 10)
      );
    });
    return sorted.map(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  function formatMoney(cents) {
    // Minimal USD money formatter. Site uses `| money` filter server-side;
    // this only runs on client-side variant switches.
    var amount = (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
    });
    return amount;
  }

  /* ───────────── Modals ───────────── */

  function wireModalOpeners() {
    var triggers = document.querySelectorAll('[data-paragon-open-modal]');
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var id = trigger.getAttribute('data-paragon-open-modal');
        openModal(id, trigger);
      });
    });
  }

  function wireModalClosers() {
    var closers = document.querySelectorAll('[data-paragon-modal-close]');
    closers.forEach(function (closer) {
      closer.addEventListener('click', function () {
        var dialog = closer.closest('dialog');
        if (dialog) closeModal(dialog);
      });
    });

    // Click-outside-to-close on native <dialog>
    var dialogs = document.querySelectorAll('dialog.paragon-modal');
    dialogs.forEach(function (dialog) {
      dialog.addEventListener('click', function (e) {
        // When clicking the backdrop, the event target is the dialog itself
        if (e.target === dialog) {
          closeModal(dialog);
        }
      });
      dialog.addEventListener('close', function () {
        document.body.classList.remove('paragon-modal-open');
        var returnFocusId = dialog.getAttribute('data-return-focus');
        if (returnFocusId) {
          var returnEl = document.getElementById(returnFocusId);
          if (returnEl && typeof returnEl.focus === 'function') {
            returnEl.focus();
          }
        }
      });
    });
  }

  function openModal(id, triggerEl) {
    var dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return;

    if (triggerEl && triggerEl.id) {
      dialog.setAttribute('data-return-focus', triggerEl.id);
    }
    document.body.classList.add('paragon-modal-open');
    dialog.showModal();
  }

  function closeModal(dialog) {
    if (typeof dialog.close === 'function') {
      dialog.close();
    }
  }

  /* ───────────── Primary action (Add to Bag or Enquire) ───────────── */

  function wirePrimaryAction() {
    var primary = document.querySelector('[data-paragon-primary-action]');
    if (!primary) return;

    primary.addEventListener('click', function (e) {
      e.preventDefault();
      var action = primary.getAttribute('data-paragon-primary-action');

      if (action === 'enquire') {
        openModal('paragon-enquire-modal', primary);
        return;
      }

      if (action === 'add-to-bag') {
        var inlineBtn = document.querySelector('[data-paragon-add-to-bag]');
        var picker = document.querySelector('[data-paragon-variant-picker]');
        var isDirty = document.body.getAttribute('data-paragon-variants-dirty') === 'true';
        var hasMultipleVariants = picker !== null;

        if (hasMultipleVariants && !isDirty) {
          // Unselected variant UX: auto-scroll + flash
          flashVariantPicker(picker);
          return;
        }

        if (inlineBtn && !inlineBtn.disabled) {
          inlineBtn.click();
        }
      }
    });
  }

  function flashVariantPicker(picker) {
    try {
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (err) {
      picker.scrollIntoView();
    }
    picker.classList.add('paragon-variant-picker--attention');
    setTimeout(function () {
      picker.classList.remove('paragon-variant-picker--attention');
    }, FLASH_DURATION_MS);
  }

  /* ───────────── Variant dirty tracking ───────────── */

  function wireVariantDirtyTracking() {
    var picker = document.querySelector('[data-paragon-variant-picker]');
    if (!picker) {
      // Product has a single variant — no selection required, already "satisfied"
      document.body.setAttribute('data-paragon-variants-dirty', 'true');
      return;
    }

    document.body.setAttribute('data-paragon-variants-dirty', 'false');

    picker.addEventListener('change', function () {
      document.body.setAttribute('data-paragon-variants-dirty', 'true');
    });

    // Listen for Dawn's variant update event so we catch programmatic changes too
    document.addEventListener('variant:change', function () {
      document.body.setAttribute('data-paragon-variants-dirty', 'true');
    });
  }
})();
